import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/api/client";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { wsPipeline, wsLabel } from "@/lib/workspace";
import { AlarmClock, Clock, Moon, CheckCircle2, Loader2, ClipboardCheck } from "lucide-react";

const PENDING_KEY = "avida_briefing_pending";

function Row({ testid, children, accent, onClick, actions }) {
  return (
    <li
      data-testid={testid}
      className={`flex items-center gap-2 px-3 py-2.5 rounded-md bg-white border ${accent}`}
    >
      <div
        onClick={onClick}
        title="Open this lead"
        className="flex items-start gap-3 flex-1 min-w-0 cursor-pointer hover:opacity-80"
      >
        {children}
      </div>
      {actions}
    </li>
  );
}

function ActBtn({ testid, onClick, icon: Icon, label }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      title={label}
      className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-1 rounded border border-[#15233B]/15 text-[#15233B]/70 hover:bg-[#15233B]/5"
    >
      <Icon className="h-3 w-3" /> {label}
    </button>
  );
}

export default function LoginBriefingGate() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  // Login-triggered open (session 'pending' flag set in AuthContext on every login)
  useEffect(() => {
    const path = location.pathname;
    const pending = sessionStorage.getItem(PENDING_KEY) === "1";
    const hasWs = !!localStorage.getItem("avida_workspace");
    const blocked = path === "/login" || path === "/choose" || path.startsWith("/field") || path.startsWith("/rn");
    if (user && user.role !== "field_agent" && pending && hasWs && !blocked) {
      setOpen(true);
    }
  }, [user, location.pathname]);

  // Manual reopen from the header briefing button (any time of day)
  useEffect(() => {
    const handler = () => {
      if (!user || user.role === "field_agent" || !localStorage.getItem("avida_workspace")) return;
      setData(null);
      setOpen(true);
    };
    window.addEventListener("avida-open-briefing", handler);
    return () => window.removeEventListener("avida-open-briefing", handler);
  }, [user]);

  // Fetch the briefing whenever the gate is open and has no data yet
  useEffect(() => {
    if (!open || data || loading) return;
    setLoading(true);
    api
      .get("/me/briefing", { params: { pipeline: wsPipeline() } })
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, data, loading]);

  const ack = async () => {
    setBusy(true);
    try {
      await api.post("/me/briefing/ack", { pipeline: wsPipeline(), counts: data?.counts });
    } catch {
      /* ack is best-effort; never block the user */
    }
    sessionStorage.removeItem(PENDING_KEY);
    setBusy(false);
    setOpen(false);
    setData(null);
    window.dispatchEvent(new Event("avida-briefing-updated"));
  };

  const openCard = async (cardId) => {
    // Acknowledge (best-effort) then jump straight to the lead's card on the board.
    try {
      await api.post("/me/briefing/ack", { pipeline: wsPipeline(), counts: data?.counts });
    } catch {
      /* best-effort */
    }
    sessionStorage.removeItem(PENDING_KEY);
    setOpen(false);
    setData(null);
    window.dispatchEvent(new Event("avida-briefing-updated"));
    navigate(`/?card=${cardId}`);
  };

  const removeItem = (section, cardId) => {
    setData((d) => {
      if (!d) return d;
      const next = { ...d, [section]: (d[section] || []).filter((x) => x.card_id !== cardId) };
      next.counts = { due: next.due.length, overdue: next.overdue.length, snoozed: next.snoozed.length };
      return next;
    });
    window.dispatchEvent(new Event("avida-briefing-updated"));
  };
  const doSnooze = async (section, c) => {
    try {
      await api.post(`/cards/${c.card_id}/snooze`, { days: 30, reason: "Snoozed from briefing" });
      toast.success(`Snoozed ${c.card_full_name} for 30 days`);
      removeItem(section, c.card_id);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const doNextStep = async (section, c) => {
    const d = new Date(); d.setDate(d.getDate() + 1);
    const iso = d.toISOString().slice(0, 10);
    try {
      await api.patch(`/cards/${c.card_id}`, { next_action_date: iso });
      toast.success("Next step set for tomorrow");
      removeItem(section, c.card_id);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const doDone = async (section, c) => {
    try {
      await api.patch(`/cards/${c.card_id}`, { next_action_date: "" });
      toast.success("Marked done");
      removeItem(section, c.card_id);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const doWake = async (c) => {
    try {
      await api.post(`/cards/${c.card_id}/unsnooze`);
      toast.success("Woken from snooze");
      removeItem("snoozed", c.card_id);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!open) return null;

  const todayLabel = new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
  const due = data?.due || [];
  const overdue = data?.overdue || [];
  const snoozed = data?.snoozed || [];
  const actionable = due.length + overdue.length;
  const allClear = !loading && actionable === 0 && snoozed.length === 0;
  const dispStage = (n) => (n > 100 ? n - 100 : n);

  return (
    <div
      data-testid="briefing-gate"
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-[#15233B]/80 backdrop-blur-sm"
    >
      <div className="w-full max-w-lg bg-[#F6F3EC] rounded-2xl shadow-2xl border border-white/10 overflow-hidden flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="px-6 py-5 bg-[#15233B] text-white relative">
          <span className="absolute top-0 left-0 right-0 h-1.5 bg-[#FFCB05]" />
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold text-[#FFCB05]">
            <ClipboardCheck className="h-4 w-4" /> Morning briefing · {wsLabel()}
          </div>
          <h2 className="mt-1 text-2xl font-extrabold tracking-tight">
            {user?.name?.split(" ")[0] ? `${user.name.split(" ")[0]}, here's your list` : "Here's your list"}
          </h2>
          <p className="text-xs text-white/70 mt-1">
            {todayLabel} — review what's assigned to you, then acknowledge to continue.
          </p>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {loading && (
            <div className="flex items-center justify-center gap-2 py-10 text-sm font-semibold text-[#15233B]/60">
              <Loader2 className="h-5 w-5 animate-spin text-[#1E73C9]" /> Pulling your items…
            </div>
          )}

          {!loading && allClear && (
            <div data-testid="briefing-empty" className="flex flex-col items-center text-center gap-2 py-8">
              <CheckCircle2 className="h-10 w-10 text-[#166534]" />
              <div className="text-lg font-extrabold text-[#15233B]">You're all clear</div>
              <div className="text-sm text-[#15233B]/60">Nothing due, overdue, or snoozed in this workspace. Have a great day.</div>
            </div>
          )}

          {!loading && overdue.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-2">
                <AlarmClock className="h-4 w-4 text-[#b91c1c]" />
                <div className="text-[11px] uppercase tracking-[0.2em] font-extrabold text-[#b91c1c]">
                  Overdue · {overdue.length}
                </div>
              </div>
              <ul className="space-y-2">
                {overdue.map((c) => (
                  <Row key={c.card_id} testid="briefing-overdue-item" accent="border-[#b91c1c]/25" onClick={() => openCard(c.card_id)}
                    actions={
                      <div className="shrink-0 flex items-center gap-1">
                        <ActBtn testid={`briefing-next-${c.card_av_id}`} onClick={() => doNextStep("overdue", c)} icon={Clock} label="Next" />
                        <ActBtn testid={`briefing-snooze-${c.card_av_id}`} onClick={() => doSnooze("overdue", c)} icon={Moon} label="Snooze" />
                      </div>
                    }
                  >
                    <span className="mt-0.5 shrink-0 text-[10px] font-bold px-2 py-0.5 rounded bg-[#b91c1c] text-white">
                      {Math.round(c.days_over)}d over
                    </span>
                    <div className="min-w-0">
                      <div className="font-bold text-sm text-[#15233B] truncate">{c.card_full_name}</div>
                      <div className="text-[11px] text-[#15233B]/55">
                        <span className="font-mono">{c.card_av_id}</span> · {dispStage(c.card_stage)} {c.stage_name} · {c.days_in_stage}d / target {c.target}d
                      </div>
                    </div>
                  </Row>
                ))}
              </ul>
            </section>
          )}

          {!loading && due.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-2">
                <Clock className="h-4 w-4 text-[#b45309]" />
                <div className="text-[11px] uppercase tracking-[0.2em] font-extrabold text-[#b45309]">
                  Due · {due.length}
                </div>
              </div>
              <ul className="space-y-2">
                {due.map((c) => (
                  <Row key={c.card_id} testid="briefing-due-item" accent="border-[#FBBF24]/50" onClick={() => openCard(c.card_id)}
                    actions={
                      <div className="shrink-0 flex items-center gap-1">
                        <ActBtn testid={`briefing-done-${c.card_av_id}`} onClick={() => doDone("due", c)} icon={CheckCircle2} label="Done" />
                        <ActBtn testid={`briefing-next-${c.card_av_id}`} onClick={() => doNextStep("due", c)} icon={Clock} label="+1d" />
                        <ActBtn testid={`briefing-snooze-${c.card_av_id}`} onClick={() => doSnooze("due", c)} icon={Moon} label="Snooze" />
                      </div>
                    }
                  >
                    <span className="mt-0.5 shrink-0 text-[10px] font-bold px-2 py-0.5 rounded bg-[#FFCB05] text-[#15233B]">
                      {c.next_action_date}
                    </span>
                    <div className="min-w-0">
                      <div className="font-bold text-sm text-[#15233B] truncate">{c.card_full_name}</div>
                      <div className="text-[11px] text-[#15233B]/55 truncate">
                        <span className="font-mono">{c.card_av_id}</span> · {dispStage(c.card_stage)} {c.stage_name}
                        {c.next_action_text ? ` · ${c.next_action_text}` : ""}
                      </div>
                    </div>
                  </Row>
                ))}
              </ul>
            </section>
          )}

          {!loading && snoozed.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-2">
                <Moon className="h-4 w-4 text-[#4F46E5]" />
                <div className="text-[11px] uppercase tracking-[0.2em] font-extrabold text-[#4F46E5]">
                  Snoozed · {snoozed.length}
                </div>
              </div>
              <ul className="space-y-2">
                {snoozed.map((c) => (
                  <Row key={c.card_id} testid="briefing-snoozed-item" accent="border-[#4F46E5]/25" onClick={() => openCard(c.card_id)}
                    actions={
                      <div className="shrink-0 flex items-center gap-1">
                        <ActBtn testid={`briefing-wake-${c.card_av_id}`} onClick={() => doWake(c)} icon={AlarmClock} label="Wake" />
                      </div>
                    }
                  >
                    <span className="mt-0.5 shrink-0 text-[10px] font-bold px-2 py-0.5 rounded bg-[#4F46E5] text-white">
                      wakes {c.snoozed_until}
                    </span>
                    <div className="min-w-0">
                      <div className="font-bold text-sm text-[#15233B] truncate">{c.card_full_name}</div>
                      <div className="text-[11px] text-[#15233B]/55 truncate">
                        <span className="font-mono">{c.card_av_id}</span> · {dispStage(c.card_stage)} {c.stage_name}
                        {c.snooze_reason ? ` · ${c.snooze_reason}` : ""}
                      </div>
                    </div>
                  </Row>
                ))}
              </ul>
            </section>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-[#15233B]/10 bg-white flex items-center justify-between gap-3">
          <div className="text-xs font-semibold text-[#15233B]/60">
            {loading ? "" : `${actionable} to action${snoozed.length ? ` · ${snoozed.length} snoozed` : ""}`}
          </div>
          <button
            data-testid="briefing-ack-button"
            onClick={ack}
            disabled={busy || loading}
            className="px-5 py-2.5 rounded-md bg-[#166534] hover:bg-[#14532d] text-white text-sm font-extrabold inline-flex items-center gap-2 shadow-sm disabled:opacity-60"
          >
            <CheckCircle2 className="h-4 w-4" />
            {busy ? "Saving…" : "I've reviewed my list — continue"}
          </button>
        </div>
      </div>
    </div>
  );
}
